<script setup lang="ts">

	import { useFormContainer } from "~/composables/useFormContainer";
	import EmailAutocomplete from "~/components/inputs/EmailAutocomplete.vue";
	import RadioButton from "~/components/inputs/RadioButton.vue";
	import Button from "~/components/inputs/Button.vue";
	import SelectedAmount from "~/components/ui/SelectedAmount.vue";
	import { searchEmail } from "~/utils/searchAutocomplete";

	import Validator from "~/utils/validator";

	const locale = await getLocale("forms", "email_privacy");
	import { useVuelidate } from "@vuelidate/core";
	import { yesNoOptions } from "~/utils/values";
	import emailPrivacyFormRules from "~/rules/emailPrivacy";
	import Perks from "~/components/static/Perks.vue";

	defineComponent({
		name: "EmailPrivacy",
	});

	const { hasPrivacyWa } = defineProps({
		progress: { type: Number, required: true },
		showAmountDetails: { type: Boolean, default: false },
		hasPrivacyWa: { type: Boolean, default: false }
	});
	

	const { FormContainer } = useFormContainer();

	const emit = defineEmits(["init", "submit"]);

	const credit = useState<number>("credit");
	const rate = useState<number>("rate");
	const hasPowerCreditUpgrade = useState<boolean>('hasPowerCreditUpgrade');

	const model = computed(() => {
		if(hasPowerCreditUpgrade.value) {
			return getPromoUpgradeModel();
		}
		if (isPromo(credit.value, rate.value)) {
			return getPromoDefaultModelFromCredit(credit.value, rate.value)
		}
		return getModel(credit.value, rate.value)
	})

	const koModel = computed(() => {
		const model = getModelPromoKO()
		// if the selected offer has a ko model, return it
		if (isPromo(credit.value, rate.value)) {
			return model
		// if the there is a ko model with same amount but different rate, return the model without secci
		} else if (model) {
			model.secci_url = ''
			return model
		}
		return undefined
	})

	const email = useState<string>("email", () => "");
	const privacy1 = useState<boolean | null>("privacy1", () => null);
	const privacyWa = useState<boolean | null>("privacyWa", () => null);
	const privacy2 = useState<boolean | null>("privacy2", () => null);
	const privacy3 = useState<boolean | null>("privacy3", () => null);
	const privacy4 = useState<boolean | null>("privacy4", () => null);

	const v$ = useVuelidate(emailPrivacyFormRules, {
		email,
		privacy1,
		privacyWa,
		privacy2,
		privacy3,
		privacy4,
		hasPrivacyWa
	});

	const validate = () => {
		v$.value.$touch();
		v$.value.$validate();
		return !v$.value.$error;
	};
	const submit = () => {
		if (validate()) {
			emit("submit");
		}
	};
	onMounted(() => emit("init"));
</script>

<template>
	<FormContainer :progress="progress">
		<form class="form-container mt-4" @submit.prevent="submit">
			<SelectedAmount 
				:value="credit" 
				:model="model ?? undefined" 
				:ko-taeg="koModel?.taeg" 
				:ko-secci="koModel?.secci_url ?? undefined" 
				:custom-class="'text-xl'" 
				:has-details="showAmountDetails"
			>
				<template v-if="$slots.header" #header>
					<slot name="header" />
				</template>
			</SelectedAmount>
			<div
				class="flex flex-wrap text-gray-700 p-4 pb-4 px-4 sm:px-6 lg:px-12 rounded-b-lg"
			>
				<h1 class="pageTitle">
					{{ locale.title }}
				</h1>
				<p class="pageSubtitle" v-html="locale.subtitle" />

				<div class="w-full lg:w-1/2 lg:pr-2 mb-6">
					<EmailAutocomplete
						v-model="email"
						:uppercase="false"
						:searchFunction="searchEmail"
						:label="locale.email"
						:error="v$.email.$error"
						:error-message="Validator.message(v$.email)"
						test-id="email"
						:type="'email'"
					/>
					<div class="text-sm italic" v-html="locale.email_label"/>
				</div>

				<div class="flex flex-wrap sm:flex-nowrap mb-6">
					<RadioButton
						v-model="privacy1"
						:values="yesNoOptions"
						:error="v$.privacy1.$error || (privacy1 !== null && !privacy1)"
						:error-message="
							v$.privacy1.$error ? Validator.message(v$.privacy1)
							: privacy1 !== null && !privacy1 ? locale.privacy1.required
							: ''
						"
						data-testid="privacy1"
					>
						<div class="w-full sm:flex-grow order-1 sm:order-3 mb-2 text-sm" v-html="locale.privacy1.text" />
					</RadioButton>
				</div>
				<div v-if="hasPrivacyWa" class="flex flex-wrap sm:flex-nowrap mb-6">
					<RadioButton
						v-model="privacyWa"
						:values="yesNoOptions"
						:error="v$.privacyWa.$error || (privacyWa !== null && !privacyWa)"
						:error-message="
							v$.privacyWa.$error ? Validator.message(v$.privacyWa)
							: privacyWa !== null && !privacyWa ? locale.privacyWa.required
							: ''
						"
						data-testid="privacyWa"
					>
						<div class="w-full sm:flex-grow order-1 sm:order-3 mb-2 text-sm" v-html="locale.privacyWa.text" />
					</RadioButton>
				</div>
				<div class="flex flex-wrap sm:flex-nowrap mb-6">
					<RadioButton
						v-model="privacy2"
						:values="yesNoOptions"
						:error="v$.privacy2.$error || (privacy2 !== null && !privacy2)"
						:error-message="
							v$.privacy2.$error ? Validator.message(v$.privacy2)
							: privacy2 !== null && !privacy2 ? locale.privacy2.required
							: ''
						"
						data-testid="privacy2"
					>
						<div class="w-full sm:flex-grow order-1 sm:order-3 mb-2 text-sm" v-html="locale.privacy2.text" />
					</RadioButton>
				</div>
				<div class="flex flex-wrap sm:flex-nowrap mb-6">
					<RadioButton
						v-model="privacy3"
						:values="yesNoOptions"
						:error="v$.privacy3.$error || (privacy3 !== null && !privacy3)"
						:error-message="
							v$.privacy3.$error ? Validator.message(v$.privacy3)
							: privacy3 !== null && !privacy3 ? locale.privacy3.required
							: ''
						"
						data-testid="privacy3"
					>
						<div class="w-full sm:flex-grow order-1 sm:order-3 mb-2 text-sm" v-html="locale.privacy3.text" />
					</RadioButton>
				</div>
				<div class="flex flex-wrap sm:flex-nowrap mb-6">
					<RadioButton
						v-model="privacy4"
						:values="yesNoOptions"
						:error="v$.privacy4.$error || (privacy4 !== null && !privacy4)"
						:error-message="
							v$.privacy4.$error ? Validator.message(v$.privacy4)
							: privacy4 !== null && !privacy4 ? locale.privacy4.required
							: ''
						"
						data-testid="privacy4"
					>
						<div class="w-full sm:flex-grow order-1 sm:order-3 mb-2 text-sm" v-html="locale.privacy4.text" />
					</RadioButton>
				</div>
			</div>

			<div class="form-footer text-center relative mb-6">
				<Button data-testid="emailPrivacySubmit" class="!px-16" type="submit">
					<span>{{ locale.submit }}</span>
				</Button>
			</div>
		</form>
		<div class="spacer h-6" />
	</FormContainer>
  	<Perks perk-class="-mt-6"/>
</template>

<style scoped></style>
